import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Sparkles, Music2, Heart, BarChart3, QrCode,
  Palette, Zap, Layers, Globe, Calendar, Mail, Euro, Check
} from 'lucide-react';
import { Nav, Footer } from './LandingPage';
import { ComparisonTable } from '../components/seo/ComparisonTable';
import { FaqSection } from '../components/seo/FaqSection';

const ICONS = {
  sparkles: Sparkles, music: Music2, heart: Heart, chart: BarChart3,
  qrcode: QrCode, palette: Palette, zap: Zap, layers: Layers,
  globe: Globe, calendar: Calendar, mail: Mail, euro: Euro,
};

const DISPLAY = 'font-display font-extrabold tracking-tighter';
const BODY = 'font-body';
const METALLIC = 'text-transparent bg-clip-text bg-gradient-to-br from-gray-900 via-gray-600 to-gray-900';
const METALLIC_DARKBG = 'text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/40';

const CtaButtons = ({ secondary, onDark = false }) => (
  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
    <Link to="/signup" className="group">
      <button className={`${BODY} w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-7 py-4 rounded-full hover:bg-[#3377FF] transition-colors shadow-[0_4px_14px_rgba(0,85,255,0.3)] hover:shadow-[0_8px_24px_rgba(0,85,255,0.4)] hover:scale-[1.02]`}
        data-testid="seo-signup-btn">
        Create your free page
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </button>
    </Link>
    {secondary && (
      <Link to={secondary.to}>
        <button className={`${BODY} w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-semibold px-6 py-3.5 rounded-full transition-colors ${
          onDark
            ? 'text-white border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/30'
            : 'text-gray-900 border border-gray-200 bg-white hover:bg-[#C9DEF9] hover:border-gray-300 shadow-sm'
        }`}
          data-testid="seo-secondary-btn">
          {secondary.label}
          <ArrowUpRight className="w-4 h-4" />
        </button>
      </Link>
    )}
  </div>
);

export default function SeoLanding({ page }) {
  const location = useLocation();
  const canonical = `https://linkmonkey.io${location.pathname}`;
  const secondary = page.example
    ? { to: `/${page.example.username}`, label: page.example.label }
    : { to: '/examples', label: 'See examples' };

  return (
    <div className="min-h-screen bg-[#D9E9FC] text-gray-950 antialiased selection:bg-gray-950 selection:text-white" data-testid={`seo-page-${page.slug.replace('/', '-')}`}>
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.metaDescription} />
        <meta property="og:url" content={canonical} />
        <link rel="preconnect" href="https://api.fontshare.com" crossOrigin="anonymous" />
        <link href="https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@400,500,700,800,900,400i&f[]=satoshi@400,500,700&display=swap" rel="stylesheet" />
      </Helmet>

      <Nav />

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-20 md:pb-28 px-6 md:px-10 overflow-hidden bg-[#D9E9FC]">
        <div className="absolute inset-0 -z-10 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(0,85,255,0.08) 0%, transparent 65%)' }} />
        <div className="max-w-4xl mx-auto text-center">
          <p className={`${BODY} inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gray-600 bg-white border border-gray-200 rounded-full px-3.5 py-1.5 mb-8 shadow-sm`}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
            {page.eyebrow}
          </p>
          <h1 className={`${DISPLAY} text-[clamp(2.6rem,7vw,5.5rem)] leading-[0.95] mb-7`}>
            <span className={`block ${METALLIC}`}>{page.h1[0]}</span>
            <span className="block italic font-light tracking-tight text-[#0055FF]">{page.h1[1]}</span>
          </h1>
          <p className={`${BODY} text-gray-600 text-base md:text-lg leading-relaxed mb-9 max-w-2xl mx-auto`}>{page.sub}</p>
          <div className="flex justify-center mb-8">
            <CtaButtons secondary={secondary} />
          </div>
          <div className="flex flex-wrap justify-center gap-2.5">
            {page.bullets.map((b, i) => (
              <span key={i} className={`${BODY} inline-flex items-center gap-1.5 text-xs text-gray-600 px-3.5 py-1.5 rounded-full border border-gray-200 bg-white shadow-sm`}>
                <Check className="w-3 h-3 text-emerald-500" /> {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="relative py-20 md:py-28 px-6 md:px-10 bg-[#F1F7FF] border-y border-blue-100">
        <div className="max-w-6xl mx-auto">
          <h2 className={`${DISPLAY} text-[clamp(1.8rem,4vw,3rem)] leading-tight mb-12 max-w-2xl`}>
            <span className={METALLIC}>Everything you need in one creator page.</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {page.features.map((f, i) => {
              const Icon = ICONS[f.icon] || Sparkles;
              return (
                <div key={i} className="rounded-2xl border border-gray-200 bg-[#D9E9FC] p-7 hover:border-gray-300 hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.06)] transition-[border-color,box-shadow]">
                  <Icon className="w-5 h-5 text-gray-500 mb-4" strokeWidth={1.5} />
                  <h3 className={`${DISPLAY} text-gray-900 text-xl mb-2 tracking-tight`}>{f.title}</h3>
                  <p className={`${BODY} text-gray-600 text-sm leading-relaxed`}>{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison */}
      {page.comparison && (
        <section className="relative py-20 md:py-28 px-6 md:px-10 bg-[#D9E9FC]">
          <div className="max-w-4xl mx-auto">
            <h2 className={`${DISPLAY} text-[clamp(1.8rem,4vw,3rem)] leading-tight mb-10`}>
              <span className={METALLIC}>LinkMonkey vs {page.comparison.competitor}</span>
            </h2>
            <ComparisonTable competitor={page.comparison.competitor} rows={page.comparison.rows} />
          </div>
        </section>
      )}

      <FaqSection faqs={page.faqs} />

      {/* Final CTA — dark accent band */}
      <section className="relative py-24 md:py-32 px-6 md:px-10 bg-[#06070C] overflow-hidden">
        <div className="absolute inset-0 -z-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 55% at 50% 100%, rgba(0,85,255,0.18) 0%, transparent 65%)' }} />
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className={`${DISPLAY} text-[clamp(2rem,5vw,4rem)] leading-[0.95] mb-5`}>
            <span className={METALLIC_DARKBG}>{page.cta.headline}</span>
          </h2>
          <p className={`${BODY} text-white/60 text-base md:text-lg mb-9`}>{page.cta.sub}</p>
          <div className="flex justify-center">
            <CtaButtons secondary={{ to: '/pricing', label: 'See pricing' }} onDark />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
